import * as Sentry from '@sentry/react'
import type { DefaultError } from '@tanstack/react-query'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { isAuthError, isExpectedError, isForbiddenError } from '../error-utils'
import { getCurrentUserQueryKey } from './api-hooks'

const APP_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined

/**
 * The application's react-query client.
 *
 * A factory rather than a module-level singleton so tests can build one per case, and so the
 * cache-wide policies below are the same ones the app runs rather than a reconstruction of them.
 */
export const createQueryClient = () => {
  const queryClient: QueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        gcTime: 300_000,
        throwOnError: (error: DefaultError) => {
          if (!isExpectedError(error)) {
            Sentry.captureException(error)
          }

          return isAuthError(error)
        },
        retry: (failureCount, error: DefaultError) => {
          if (isForbiddenError(error) || isAuthError(error)) {
            return false
          }

          return failureCount < 2
        },
        queryKeyHashFn: queryKey => {
          const keyWithVersion = [...queryKey, APP_VERSION]
          return JSON.stringify(keyWithVersion)
        },
      },
    },
    queryCache: new QueryCache(),
    mutationCache: new MutationCache({
      // A 403 on any write is the server saying our cached authorization is stale, so the role is
      // re-fetched rather than left to keep offering a control the API refuses. It lives here
      // instead of in the write hooks because the staleness is a property of the role, not of any
      // one endpoint: a hook that opts in per call site is a hook someone can forget to opt in.
      //
      // Costs one `GET /api/v1/users/me` on a 403 that turns out to be about the resource rather
      // than the role. That is cheap and idempotent, which is why the broad rule wins over an
      // accurate one that has to be maintained 30 times.
      onError: error => {
        if (isForbiddenError(error)) {
          queryClient.invalidateQueries({ queryKey: getCurrentUserQueryKey() })
        }
      },
    }),
  })

  return queryClient
}
